import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import Loader from "../../utils/Loader";
import { ToastContainer } from "react-toastify";
import { FaCloudUploadAlt, FaTimes } from "react-icons/fa";

export default function EditProfile() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    phone: "",
    image: "",
  });

  const [tempFile, setTempFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm((prev) => ({
      email: state?.email || "",
      phone: state?.phone || "",
      image: state?.image || "",
    }));
  }, [state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTempFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const cancelUpload = () => {
    setTempFile(null);
    setPreviewUrl(null);
  };

  const handleImageUploadToCloud = async () => {
    if (!tempFile) return;

    setIsUploading(true);
    try {
      const res = await uploadFiles({ fieldName: "file", files: [tempFile] });

      if (res.error === true) {
        showErrorToast(res.message || "Failed To Upload Files!");
        setIsUploading(false);
        return;
      }

      setForm((prev) => ({ ...prev, image: res.data.imageUrl }));
      setTempFile(null);
      setPreviewUrl(null);
      showSuccessToast(
        "Image uploaded successfully! Now you can save changes."
      );
    } catch (error) {
      console.log("file upload error: ", error);
      showErrorToast(error.message || "Failed To Upload Image Error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await makeRequestAuth("post", "/admin/updateprofile", form);

      if (res?.error === false) {
        showSuccessToast("Profile updated successfully!");
        setTimeout(() => navigate("/settings/profile"), 800);
      } else {
        showErrorToast(res?.message || "Update failed!");
      }
    } catch (err) {
      showErrorToast(err?.message || "Server Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {(loading || isUploading) && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <Loader />
          {isUploading && (
            <p className="text-white mt-4 ml-2">Uploading Image...</p>
          )}
        </div>
      )}
      <ToastContainer />

      <div className="p-6 md:p-10 bg-white dark:bg-[#131A28] border border-gray-300 dark:border-white/10 rounded-3xl mx-4 shadow-lg">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-8">
          Edit Profile
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div>
            <label className="block text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full p-3 rounded-lg bg-white dark:bg-[#1F2533] border border-gray-300 dark:border-white/10 text-gray-800 dark:text-white outline-none"
            />
          </div>

          {/* Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 dark:text-gray-300 mb-1">
                Phone Number
              </label>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full p-3 rounded-lg bg-white dark:bg-[#1F2533] border border-gray-300 dark:border-white/10 text-gray-800 dark:text-white outline-none"
              />
            </div>
          </div>

          {/* Profile Picture Upload Section */}
          <div>
            <label className="block text-gray-700 dark:text-gray-300 mb-1">
              Profile Picture
            </label>

            {/* File Input */}
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleFileSelect}
              className="w-full p-3 rounded-lg bg-white dark:bg-[#1F2533] border 
               border-gray-300 dark:border-white/10 text-gray-800 dark:text-white outline-none"
            />

            {/* Preview Area */}
            <div className="mt-4 flex items-center gap-6">
              {/* Scenario 1: Pending Upload (Local Preview) */}
              {tempFile && previewUrl && (
                <div className="relative">
                  <p className="text-sm text-yellow-600 mb-1">
                    Preview (Not Uploaded Yet):
                  </p>
                  <img
                    src={previewUrl}
                    alt="Local Preview"
                    className="w-24 h-24 rounded-full object-cover border-4 border-yellow-400"
                  />
                  <button
                    type="button"
                    onClick={cancelUpload}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 text-xs"
                    title="Cancel Upload"
                  >
                    <FaTimes />
                  </button>
                </div>
              )}

              {!tempFile && form.image && (
                <div>
                  <p className="text-sm text-green-600 mb-1">Current Image:</p>
                  <img
                    src={form.image}
                    alt="Current"
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
            {tempFile ? (
              <button
                type="button"
                onClick={handleImageUploadToCloud}
                className="flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg font-medium transition"
              >
                <FaCloudUploadAlt className="text-xl" /> Upload Image First
              </button>
            ) : (
              <button
                type="submit"
                className="bg-[#5B3BFF] hover:bg-[#4a2fd1] text-white py-3 rounded-lg font-medium transition"
              >
                Save Changes
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate("/settings/profile")}
              className="bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
